<script setup lang="ts">
import {
  CloudSun,
  Map,
  Info,
  Settings,
  ChartNoAxesCombined,
  Menu,
} from "@lucide/vue";
import { RouterLink, useRoute } from "vue-router";
import { ref, watch, onMounted, onUnmounted } from "vue";

const props = defineProps<{ compact?: boolean }>();
const open = ref(false);
const container = ref<HTMLElement | null>(null);
const trigger = ref<HTMLButtonElement | null>(null);
const route = useRoute();

const links = [
  { to: "/", label: "Home", icon: CloudSun },
  { to: "/map", label: "Map", icon: Map },
  {
    to: "/ensembles",
    label: "Models",
    title: "Ensemble forecasts",
    icon: ChartNoAxesCombined,
  },
  { to: "/about", label: "About", icon: Info },
  { to: "/settings", label: "Settings", icon: Settings },
];

/** Dismiss navigation when a pointer event occurs outside it. */
function onOutside(event: PointerEvent) {
  if (!container.value?.contains(event.target as Node)) open.value = false;
}

/** Dismiss navigation and return focus to its trigger. */
function onEscape() {
  open.value = false;
  trigger.value?.focus();
}

watch(
  () => route.path,
  () => {
    open.value = false;
  },
);

watch(
  () => props.compact,
  () => {
    open.value = false;
  },
);

onMounted(() => document.addEventListener("pointerdown", onOutside));

onUnmounted(() => document.removeEventListener("pointerdown", onOutside));
</script>

<template>
  <nav
    ref="container"
    class="z-60 flex gap-1 md:flex-col md:gap-2"
    :class="
      compact
        ? 'relative inset-auto m-0 w-auto border-0 bg-transparent p-0 shadow-none'
        : 'fixed inset-x-3 bottom-[max(10px,env(safe-area-inset-bottom))] mx-auto w-auto max-w-[460px] rounded-panel border border-app-borderSubtle bg-app-conditions p-[5px] shadow-[0_2px_8px_#0000000c] md:inset-y-4 md:right-auto md:left-4 md:w-[76px] md:rounded-shell md:px-1.5 md:py-2.5'
    "
    aria-label="Primary navigation"
    @keydown.esc.stop="onEscape"
  >
    <button
      v-if="compact"
      ref="trigger"
      class="grid size-11 cursor-pointer place-items-center rounded-control text-text-primary hover:bg-app-elevated focus-visible:outline-2 focus-visible:outline-accent"
      type="button"
      aria-label="Navigation menu"
      :aria-expanded="open"
      aria-controls="primary-navigation-links"
      @click="open = !open"
    >
      <Menu :size="22" aria-hidden="true" />
    </button>
    <RouterLink
      v-if="!compact"
      to="/"
      class="mb-2 hidden min-h-15 items-center justify-center rounded-control focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navigation-active md:flex"
      aria-label="RainOwls home"
    >
      <img class="size-[38px]" src="@/assets/icons/AppIcon.svg" alt="" />
    </RouterLink>
    <div
      v-show="!compact || open"
      id="primary-navigation-links"
      :class="
        compact
          ? 'absolute top-[calc(100%+8px)] left-0 flex w-[200px] flex-col gap-1 rounded-panel border border-app-border bg-app-conditions p-2 shadow-[0_12px_30px_#0005]'
          : 'contents'
      "
    >
      <RouterLink
        v-for="link in links"
        :key="link.to"
        :to="link.to"
        :title="link.title"
        class="selection-edge flex items-center rounded-control border border-transparent text-navigation-ink transition-colors duration-[140ms] ease-[ease] [--selection-border-width:1px] [--selection-surface-shadow:none] hover:bg-app-elevated hover:text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navigation-active motion-reduce:transition-none [&.is-selected]:bg-app-selected [&.is-selected]:text-text-primary"
        :class="[
          compact
            ? 'min-h-11 flex-row justify-start gap-3 px-3 py-2 text-[14px]'
            : 'min-h-[54px] flex-1 flex-col justify-center gap-[3px] text-[10px] md:min-h-[66px] md:flex-none md:gap-[7px] md:text-[11px]',
          { 'md:mt-auto': !compact && link.to === '/about' },
        ]"
        exact-active-class="is-selected"
      >
        <component
          :is="link.icon"
          :size="23"
          class="max-md:size-[21px]"
          :stroke-width="1.65"
          aria-hidden="true"
        />
        <span>{{ link.label }}</span>
      </RouterLink>
    </div>
  </nav>
</template>
